<template>
  <SearchResultView :seo-type="seoType" :seo-override="seoOverride"
                    :share-target="isCombo ? `/list/${slugPath}` : undefined"
                    share-source="combo_share"></SearchResultView>
</template>

<script setup
        lang="ts">
import {onMounted} from "vue";
import SearchResultView from "@/components/SearchResult/SearchResultView.vue";

import {useSearchQueryStore} from "@/stores/searchQuery.js";
import {useSearchWithStore} from "@/composables/useSearchWithStore";
import {useBasicLayoutStore} from '#imports';
import {getUsageSearchParam, cityListUrl, getComboSearchParam} from "@/config/indexSearchParam.js";
import {capacityBandSlug, COMBO_MIN_INVENTORY} from "@/config/common.js";
import {getSeoText, buildComboSeo, buildBreadcrumbTrail} from "@/config/seoText.js";
import {getSpaceTypeList, getSearchSpaceList} from "@/api/space";

definePageMeta({
  title: '搜尋結果'
})

const route = useRoute();
const searchQueryStore = useSearchQueryStore()
const {handleSearchWithStore} = useSearchWithStore()
const basicLayoutStore = useBasicLayoutStore();
const categoryStore = useCategoryStore()

// catch-all:slug 為路徑段陣列。1 段=相容舊 URL(城市或用途);2–3 段=城市/用途/人數組合。
const slug = (Array.isArray(route.params.slug) ? route.params.slug : [route.params.slug]).filter(Boolean)
const slugPath = slug.join('/')
const isCombo = slug.length > 1

basicLayoutStore.setBreadcrumb(slug[0]);

// canonical 各自指向自己的完整路徑(1 段時 = 既有 URL,不變)
useHead({
  link: [
    {
      rel: 'canonical',
      href: `https://www.onestep.place/list/${slugPath}`
    }
  ]
});

// 麵包屑 JSON-LD(schema.org BreadcrumbList):首頁 › 城市 › 城市+用途 › +人數。
// item URL 一律指向 prod(與 canonical 一致);trail 僅 1 層(未知 slug)則不輸出。
const breadcrumbTrail = buildBreadcrumbTrail(slug)
if (breadcrumbTrail.length > 1) {
  useHead({
    script: [{
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: breadcrumbTrail.map((b, i) => ({
          '@type': 'ListItem',
          position: i + 1,
          name: b.name,
          item: `https://www.onestep.place${b.path}`
        }))
      })
    }]
  })
}

// rich SEO 主體沿用「用途段」既有文案(城市段無 seoText);1 段時用該段本身。
const usageSlug = isCombo ? slug[1] : slug[0]
const richSeoText = getSeoText(usageSlug)
const seoType = [usageSlug]

// 組合頁:buildComboSeo 產生 SEO(內含 手寫覆蓋 → 模板);override 餵 SearchResultView 渲染 h1/標題/sections。
const combo = isCombo ? buildComboSeo(slug) : null
const seoOverride = combo?.override ?? null

// 頁面 <title>/<meta>:組合用 combo → 單段用既有 rich seoText → 通用 fallback
const resolvedTitle = combo?.metaTitle?.trim()
    || richSeoText?.metaTitle?.trim()
    || (richSeoText?.section1?.resultTitle
        ? `${richSeoText.section1.resultTitle}到空間便利店，3步驟輕鬆預訂！`
        : '空間便利店｜方便順利的場地租借平台');
const resolvedDescription =
    combo?.metaDescription?.trim() ||
    richSeoText?.metaDescription?.trim() ||
    '會議室租借是不少企業與團體的熱門選擇，透過租用會議空間幫助活動順利進行！下方我們整理了會議室租借常見問題，介紹空間租借方案、價格、預約流程，以及大中小型會議室租借台北推薦平台，供您參考！';

// 組合頁:OG 用 combo-specific 文案 + 完整 slug URL(與 canonical 一致),讓分享進 LINE 出豐富預覽。
// 單段頁(既有已索引):OG 完全不變(向下相容,守 Phase-1 法律)。
// ogImage 維持品牌 banner —— combo 跨多場地、無單一代表圖。
const seoMeta = {
  title: resolvedTitle,
  description: resolvedDescription,
  keywords: '場地租借、會議室、攝影棚、廚房租借、活動空間、餐廳包廂、聚會場地，都可線上預約',
  ogTitle: isCombo
      ? resolvedTitle
      : '空間便利店｜方便順利的場地租借平台。時租日租場地，活動聚會場地，商務會議線上預約',
  ogSiteName: '空間便利店',
  ogDescription: isCombo
      ? resolvedDescription
      : '空間便利店是一個線上的場地租借平台。我們提供線上預約，省去聯絡，方便又順利。活動空間、會議室，攝影棚、餐廳包廂、聚會場地都可預約。',
  ogImage: 'https://pic.onestep.place/images/onestep-og.png',
  ogImageAlt: '空間便利店｜方便順利的場地租借平台。時租日租場地，活動聚會場地，商務會議線上預約',
  ogUrl: isCombo ? `https://www.onestep.place/list/${slugPath}` : 'https://www.onestep.place',
  ogType: 'website',
};

useSeoMeta(seoMeta);

// N 守門(SSR):組合頁庫存 < N → noindex(防薄內容)。
// usage id 由 type-list 解析(共用 app.vue 的 useAsyncData('spaceTypeListData') 快取,SSR 不重抓);
// useRobotsRule 僅在 server 端設定,故須在 setup 內完成。單段頁不套用。
if (isCombo) {
  const usageCategoryId = useRuntimeConfig().public.usageCategoryId
  const {data: typeList} = await useAsyncData('spaceTypeListData', () => getSpaceTypeList())
  const usageSubs = (typeList.value?.categories?.subs ?? [])
      .find((c: {id: number}) => Number(c.id) === Number(usageCategoryId))?.subs ?? []
  const usageId = usageSubs.find((s: {slug: string; id: number}) => s.slug === slug[1])?.id ?? -1
  const cityId = cityListUrl[slug[0]]?.filter_options?.city_ids?.[0]

  if (usageId !== -1 && cityId) {
    const capacityRange = slug[2] ? capacityBandSlug[slug[2]] : null
    const {data: comboTotal} = await useAsyncData(`combo-count:${slugPath}`, () =>
        getSearchSpaceList({
          pagination: {dir: 1, limit: 1, page: 1, sort: 'created_date'},
          filter_options: {
            keyword: '',
            rent_time: {rent_type: 0, begin_time: '', end_time: ''},
            geo_search: {longitude: 0, latitude: 0, width: 0, height: 0, radius: 0},
            equipment_ids: [],
            service_ids: [],
            city_ids: [cityId],
            category_ids: {[usageCategoryId]: [usageId]},
            people_cap_range: capacityRange ?? {}
          }
        }).then((r: {total?: number}) => r?.total ?? 0)
    )
    if ((comboTotal.value ?? COMBO_MIN_INVENTORY) < COMBO_MIN_INVENTORY) {
      useRobotsRule(false)
    }
  }
}

// 1 段:城市 slug 或 用途 slug 雙查(既有邏輯,相容舊 URL)
function resolveSingleSearch() {
  const citySearch = cityListUrl[slug[0]]
  const usageId = categoryStore.getUsageCategoryIdBySlug(slug[0])
  const usageSearch = usageId !== -1 ? getUsageSearchParam(usageId) : null
  return citySearch || usageSearch
}

// 2–3 段:第 1 段=城市、第 2 段=用途、第 3 段=人數帶,合併為單一搜尋條件
function resolveComboSearch() {
  const citySearch = cityListUrl[slug[0]] || null
  const usageId = categoryStore.getUsageCategoryIdBySlug(slug[1])
  const usageSearch = usageId !== -1 ? getUsageSearchParam(usageId) : null
  const capacityRange = slug[2] ? capacityBandSlug[slug[2]] : null
  return getComboSearchParam(citySearch, usageSearch, capacityRange)
}

onMounted(() => {
  const searchParams = isCombo ? resolveComboSearch() : resolveSingleSearch();

  if (searchParams) {
    searchQueryStore.setPagination(searchParams.pagination);
    searchQueryStore.setFilterOptions(searchParams.filter_options);
    handleSearchWithStore(false);
  }
})


</script>

<style scoped
       lang="scss">

</style>
